import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import bgImage from "../assets/logo.jpg";

import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Loader2 } from "lucide-react";
import { Alert } from "@mui/material";

import { useAuth } from "../AuthProvider/AuthProvider";
import { getDefaultRoute } from "../../lib/roleUtils";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const history = useNavigate();
  const [loading, setLoading] = useState(false);
  const { setIsAuthenticated, getUserInfo } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname;

  useEffect(() => {
    const fetchTokenValidation = async () => {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          const { data } = await axios.get(
            `${backendUrl}/api/v1/auth/verifyJwtToken/${token}`
          );
          if (data === true) {
            const userDetails = await getUserInfo();
            setIsAuthenticated(true);
            history(from || getDefaultRoute(userDetails.role), { replace: true });
          }
        } catch (err) {
          console.log(err);
        }
      }
    };
    fetchTokenValidation();
  }, [getUserInfo, setIsAuthenticated, history, from]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${backendUrl}/api/v1/auth/authenticate`, {
        email: username,
        password: password,
      });
      const { access_token, refresh_token } = response.data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      const userDetails = await getUserInfo();
      setIsAuthenticated(true);
      history(from || getDefaultRoute(userDetails.role), { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "Invalid username or password!");
      setTimeout(() => setError(""), 5000); // auto-close error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex">
      {/* LEFT SIDE */}
      <div className="hidden lg:flex w-1/2 flex-col items-center justify-center bg-gradient-to-br from-black via-gray-900 to-gray-800 text-white p-10 relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.3),_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(234,60,18,0.25),_transparent_50%)]"></div>

        <img src={bgImage} alt="logo" className="w-28 mb-6 relative z-10" />
        <h1 className="text-4xl font-extrabold tracking-tight relative z-10">
          Welcome to <span style={{"color":"#e93c14"}}>CHATYFY</span>
        </h1>
        <h1 className="text-3xl font-extrabold tracking-tight relative z-10">
          <span style={{"color":"#e93c14"}}>Business of Conversations</span>
        </h1>
        <p className="mt-3 text-gray-300 max-w-md text-center relative z-10">
          We empower vendors and businesses to grow through <span className="font-semibold text-white">WhatsApp</span> — enabling seamless e-commerce, smart marketing campaigns, and automated workflows.
          <br />
          Engage your customers where conversations truly happen.
        </p>

      </div>

      {/* RIGHT SIDE */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md shadow-xl rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-md">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="font-extrabold text-3xl text-gray-800">
              Sign In
            </CardTitle>
            <p className="text-sm text-gray-500">
              Access your dashboard in seconds
            </p>
          </CardHeader>

          <CardContent className="space-y-5">
            {error && <Alert severity="error">{error}</Alert>}

            <form className="space-y-4" onSubmit={handleLogin}>
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-700 font-medium">
                  Email
                </Label>
                <Input
                  id="username"
                  type="email"
                  placeholder="you@example.com"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="rounded-xl border-gray-300 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-xl border-gray-300 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-between text-sm text-gray-600">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 accent-blue-600"
                  />
                  Remember this device
                </label>
                <a href="#" className="text-blue-600 hover:underline">
                  Forgot password?
                </a>
              </div>

              <Button
                className="w-full flex justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md transition"
                disabled={loading}
                type="submit"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Log In
              </Button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <div className="flex-1 border-t border-gray-200" />
              or
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Register button */}
            <Button
              variant="outline"
              className="w-full flex justify-center gap-2 rounded-xl border-gray-300 font-medium hover:bg-gray-100"
              onClick={() => history("/signup")}
            >
              Create Account
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
